import type React from 'react';
import { useEffect } from 'react';

import { FormField, FormHelp, Label } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import type Field from '../../../models/Field';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { PRORATED_RENT } from '../../../constants';
import { MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import CurrencyInput from '../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  [PRORATED_RENT.FIELD_NAME.IS_ENABLED]: false,
  [PRORATED_RENT.FIELD_NAME.RENT_AMOUNT]: '',
};

export default function ProratedRent({ section, setSectionValues }: Props): JSX.Element {
  useEffect(() => {
    section.setCustomFieldValidator(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT, isAmountValid);
  }, []);

  function isAmountValid(amount: string, field?: Field): boolean {
    if (Number(amount.replace(/,/g, '')) > PRORATED_RENT.BOUNDS.MAX_AMOUNT) {
      (field ?? section.getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT)).setErrorMessage(
        MAX_AMOUNT_ERROR_MESSAGE(PRORATED_RENT.BOUNDS.MAX_AMOUNT),
      );
      return false;
    }
    return true;
  }

  function onNoProrated(): void {
    setSectionValues(INITIAL_STATE);
  }

  function onYesProrated(): void {
    setSectionValues({
      [PRORATED_RENT.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onProratedRent(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({ [PRORATED_RENT.FIELD_NAME.RENT_AMOUNT]: value });
  }

  return (
    <>
      <FormField
        required
        label={<Label>Will there be any prorated rent when the tenant moves in?</Label>}
        control={
          <YesAndNoExpansion
            yesHandler={onYesProrated}
            noHandler={onNoProrated}
            yesActive={section.getFieldValue(PRORATED_RENT.FIELD_NAME.IS_ENABLED)}
          >
            <FormField
              required
              error={section.getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT).hasError}
              css={{ marginTop: 'layout.tight' }}
              label={<Label id="prorated-rent-amount">Prorated rent amount</Label>}
              control={
                <CurrencyInput
                  aria-labelledby="prorated-rent-amount"
                  data-test="amount"
                  value={section.getFieldValue(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT)}
                  onChange={onProratedRent}
                  onBlur={onProratedRent}
                  error={section.getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT).hasError}
                />
              }
              description={
                <>
                  {section.getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT).hasError && (
                    <FormHelp>{section.getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT).getErrorMessage()}</FormHelp>
                  )}
                  <FormHelp>
                    To calculate prorated rent, count the number of days between the move-in date and the due date for
                    the first full payment. Multiply that number by the daily rent amount (which is the total monthly
                    rent divided by 30 days).
                  </FormHelp>
                </>
              }
            />
          </YesAndNoExpansion>
        }
        description={
          !section.getFieldValue(PRORATED_RENT.FIELD_NAME.IS_ENABLED) && (
            <FormHelp>Prorated rent is charged from move-in until the first full payment’s due</FormHelp>
          )
        }
      />
    </>
  );
}
